/**
 * UsuarioPermisosDialog (F84) — vista + edición de permisos personalizados.
 *
 * Muestra TODOS los permisos del catálogo agrupados por módulo. Cada uno
 * tiene 3 estados visuales:
 *   • Heredado del rol (gris, switch ON, no editable directo)
 *   • Override grant (azul, switch ON, badge "+ extra")
 *   • Override revoke (rojo, switch OFF, badge "- revocado")
 *
 * Toggle: si el permiso está heredado (gris) y haces click, se crea
 * override revoke (lo revoca). Si está revoke y click, vuelve a default
 * (clear override → hereda del rol). Etc.
 */
import { useMemo } from "react";
import { toast } from "sonner";
import { Loader2, Shield, ShieldCheck, ShieldOff, Lock } from "lucide-react";

import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import type { ApiError } from "@/lib/api";
import { useAuthStore } from "@/lib/auth";

import {
  useClearOverride, useEffectivePermissions, useOverrides, usePermissions,
  useSetOverride, useUsuario,
} from "./api";

type Props = {
  userId: string | null;
  open: boolean;
  onOpenChange: (v: boolean) => void;
};

export function UsuarioPermisosDialog({ userId, open, onOpenChange }: Props) {
  const { data: user } = useUsuario(open ? userId : null);
  const { data: catalog = [], isLoading: lc } = usePermissions();
  const { data: effective = [], isLoading: le } = useEffectivePermissions(open ? userId : null);
  const { data: overrides = [], isLoading: lo } = useOverrides(open ? userId : null);

  // F104.1 — guard: nadie se puede modificar permisos a sí mismo (defensa
  // contra que un admin se revoque el permiso usuarios.write y se quede
  // bloqueado fuera del sistema). El backend también valida.
  const currentUser = useAuthStore((s) => s.user);
  const isSelf = currentUser?.id === userId;

  const setMut = useSetOverride(userId ?? "");
  const clearMut = useClearOverride(userId ?? "");

  // codigo -> {fuente, granted}
  const effectiveMap = useMemo(() => {
    const m = new Map<string, { fuente: string; granted: boolean }>();
    for (const p of effective) m.set(p.codigo, { fuente: p.fuente, granted: p.granted });
    return m;
  }, [effective]);

  const overrideMap = useMemo(() => {
    const m = new Map<string, boolean>();
    for (const o of overrides) m.set(o.permission_codigo, o.granted);
    return m;
  }, [overrides]);

  // Agrupar catálogo por módulo
  const grouped = useMemo(() => {
    const g = new Map<string, typeof catalog>();
    for (const p of catalog) {
      const arr = g.get(p.modulo) ?? [];
      arr.push(p);
      g.set(p.modulo, arr);
    }
    return Array.from(g.entries()).sort(([a], [b]) => a.localeCompare(b));
  }, [catalog]);

  async function handleClick(codigo: string) {
    if (isSelf) {
      toast.error("No puedes modificar tus propios permisos", {
        description: "Pídele a otro administrador que haga el cambio.",
      });
      return;
    }
    try {
      const eff = effectiveMap.get(codigo);
      const ov = overrideMap.get(codigo);

      if (ov === true) {
        // Estado: override grant → quitar override (vuelve a default del rol)
        await clearMut.mutateAsync(codigo);
        toast.success(`Override de ${codigo} eliminado`);
      } else if (ov === false) {
        // Estado: override revoke → quitar override (vuelve a default del rol)
        await clearMut.mutateAsync(codigo);
        toast.success(`Permiso ${codigo} restaurado`);
      } else if (eff?.granted) {
        // Estado: heredado del rol y otorgado → revocar (override=false)
        await setMut.mutateAsync({ codigo, granted: false });
        toast.success(`Permiso ${codigo} revocado para este usuario`);
      } else {
        // Estado: no lo tiene del rol → otorgar (override=true)
        await setMut.mutateAsync({ codigo, granted: true });
        toast.success(`Permiso ${codigo} otorgado a este usuario`);
      }
    } catch (e) {
      toast.error("No se pudo cambiar el permiso", {
        description: (e as ApiError).message,
      });
    }
  }

  const loading = lc || le || lo;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Shield size={16} /> Permisos de {user?.email ?? "…"}
          </DialogTitle>
          <DialogDescription>
            Hereda los permisos de su rol <Badge variant="outline" className="ml-1">
              {user?.rol_nombre ?? "—"}
            </Badge>
            . Tildando agregas un permiso extra; destildando uno heredado lo revocas
            solo para este usuario.
          </DialogDescription>
        </DialogHeader>

        {isSelf && (
          <div className="mb-2 flex items-start gap-2 rounded-md border border-amber-300 bg-amber-50 p-2 text-[11px] text-amber-900 dark:border-amber-900/40 dark:bg-amber-950/30 dark:text-amber-300">
            <Lock size={14} className="mt-0.5 shrink-0" />
            <span>
              <strong>Eres el usuario activo.</strong> Por seguridad no puedes
              modificar tus propios permisos — pídele a otro administrador que
              haga el cambio.
            </span>
          </div>
        )}

        {loading ? (
          <div className="flex items-center justify-center py-10 text-muted-foreground">
            <Loader2 size={16} className="animate-spin" />
          </div>
        ) : (
          <div className="space-y-3">
            {grouped.map(([modulo, perms]) => (
              <div key={modulo} className="rounded-lg border">
                <div className="border-b bg-muted/30 px-3 py-2 text-[10px] font-semibold uppercase text-muted-foreground">
                  {modulo}
                </div>
                <div className="divide-y">
                  {perms.map((p) => {
                    const eff = effectiveMap.get(p.codigo);
                    const ov = overrideMap.get(p.codigo);
                    const isOn = eff?.granted ?? false;
                    const fuente = ov === true ? "grant" : ov === false ? "revoke" : eff ? "rol" : "none";

                    const label =
                      fuente === "grant" ? "+ extra" :
                      fuente === "revoke" ? "− revocado" :
                      fuente === "rol" ? "del rol" : "sin acceso";

                    const badgeClass =
                      fuente === "grant" ? "bg-primary/10 text-primary border-primary/30" :
                      fuente === "revoke" ? "bg-destructive/10 text-destructive border-destructive/30" :
                      fuente === "rol" ? "bg-muted text-muted-foreground" :
                      "bg-muted/40 text-muted-foreground/60";

                    return (
                      <button
                        key={p.codigo}
                        onClick={() => handleClick(p.codigo)}
                        disabled={setMut.isPending || clearMut.isPending || isSelf}
                        className="flex w-full items-center justify-between gap-2 px-3 py-2 text-left hover:bg-muted/30 disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:bg-transparent"
                      >
                        <div className="flex items-center gap-2 flex-1">
                          {isOn ? (
                            <ShieldCheck size={14} className="text-emerald-500 shrink-0" />
                          ) : (
                            <ShieldOff size={14} className="text-muted-foreground/40 shrink-0" />
                          )}
                          <div className="flex-1">
                            <div className="text-xs font-mono">{p.codigo}</div>
                            {p.descripcion && (
                              <div className="text-[10px] text-muted-foreground">{p.descripcion}</div>
                            )}
                          </div>
                        </div>
                        <Badge variant="outline" className={`text-[9px] ${badgeClass}`}>
                          {label}
                        </Badge>
                      </button>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" size="sm" onClick={() => onOpenChange(false)}>
            Cerrar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
